import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import API_ENDPOINTS from '../config';
import logger from '../utils/logger';

// System admin whitelist - must match backend
const SYSTEM_ADMIN_EMAILS = [
  'nemanja@lizzardsolutions.com',
  'admin@lizzardsolutions.com',
  // Add more system admin emails here
];

// Check if user is a system admin
const isSystemAdmin = (email) => {
  return SYSTEM_ADMIN_EMAILS.includes(email);
};

export default function UserMenu() {
  const { t } = useTranslation();
  const { isDark, toggleDarkMode } = useTheme();
  const colors = getThemeColors(isDark);
  const [isOpen, setIsOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch user info from API (cookie-based auth)
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.PROFILE, {
          method: 'GET',
          credentials: 'include', // Include cookies
        });
        
        if (res.ok) {
          // Check if response is JSON
          const contentType = res.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            logger.error('Expected JSON but got:', contentType);
            return;
          }
          
          const data = await res.json();
        setUserInfo({
            email: data.user?.email,
            userId: data.user?.id,
            companyId: data.company?.id,
            role: data.user?.role,
            isPlatformOwner: data.company?.isPlatformOwner || false
        });
        } else if (res.status === 401) {
          // Not authenticated, redirect to login
          navigate('/login');
        } else {
          logger.error('Failed to fetch user info:', res.status, res.statusText);
        }
      } catch (error) {
        logger.error('Error fetching user info:', error);
        // Don't redirect on network errors, just log
      }
    };
    
    fetchUserInfo();
  }, [navigate]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Adjust dropdown position to prevent overflow using fixed positioning
  const updateDropdownPosition = React.useCallback(() => {
    if (isOpen && buttonRef.current && dropdownRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdown = dropdownRef.current;
      const viewportWidth = window.innerWidth;
      const viewportPadding = 10;
      const minDropdownWidth = 220;
      
      // Calculate position: align right edge of dropdown with right edge of button
      const rightPosition = viewportWidth - buttonRect.right;
      const topPosition = buttonRect.bottom + 8;
      
      // Calculate available space on the right
      const spaceOnRight = viewportWidth - buttonRect.right - viewportPadding;
      
      // Set fixed positioning
      dropdown.style.position = 'fixed';
      dropdown.style.top = `${topPosition}px`;
      dropdown.style.right = `${rightPosition}px`;
      dropdown.style.left = 'auto';
      dropdown.style.bottom = 'auto';
      
      // If there's not enough space on the right, constrain the width
      if (spaceOnRight < minDropdownWidth) {
        // Use available space, but ensure minimum width
        const maxWidth = Math.max(minDropdownWidth, spaceOnRight);
        dropdown.style.maxWidth = `${maxWidth}px`;
        dropdown.style.width = `${maxWidth}px`;
      } else {
        // Reset to default
        dropdown.style.maxWidth = 'min(320px, calc(100vw - 20px))';
        dropdown.style.width = 'auto';
      }
      
      // Ensure dropdown doesn't go below viewport
      const dropdownHeight = dropdown.offsetHeight || 400;
      if (topPosition + dropdownHeight > window.innerHeight) {
        dropdown.style.maxHeight = `${window.innerHeight - topPosition - 10}px`;
        dropdown.style.overflowY = 'auto';
      } else {
        dropdown.style.maxHeight = 'none';
        dropdown.style.overflowY = 'hidden';
      }
    }
  }, [isOpen]);

  useEffect(() => {
    updateDropdownPosition();
    
    // Update position on window resize or scroll
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  const handleLogout = async () => {
    try {
      // Call logout endpoint to clear cookie
      await fetch(API_ENDPOINTS.LOGOUT, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      logger.error('Logout error:', err);
    } finally {
      // Navigate to login regardless of API call success
    navigate('/login');
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div style={{ 
      position: 'relative',
      zIndex: 1001,
    }} ref={menuRef}>
      {/* User Avatar Button */}
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        style={{
          background: colors.backgroundHover,
          border: 'none',
          borderRadius: '50%',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: `0 1px 4px ${colors.shadow}`,
          transition: 'all 0.2s ease',
          flexShrink: 0,
          ...(isOpen && {
            background: colors.border,
            boxShadow: `0 2px 8px ${colors.shadowHover}`,
          })
        }}
        aria-label={t('userMenu.user')}
      >
        <svg width="22" height="22" fill={colors.primary} viewBox="0 0 24 24">
          <path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5z"/>
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            background: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            boxShadow: `0 4px 24px ${colors.shadowHover}`,
            minWidth: 220,
            maxWidth: 'min(320px, calc(100vw - 20px))',
            width: 'auto',
            zIndex: 10000,
            overflow: 'hidden',
          }}
        >
          {/* User Info Section */}
          {userInfo && (
            <div style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${colors.borderLight}`,
              background: colors.backgroundLight
            }}>
              <div style={{
                fontSize: 14,
                fontWeight: 600,
                color: colors.primary,
                marginBottom: 4
              }}>
                {t('userMenu.loggedInAs')}
              </div>
              <div style={{
                fontSize: 15,
                color: colors.text,
                fontWeight: 500,
                wordBreak: 'break-word'
              }}>
                {userInfo.email}
              </div>
              {userInfo.role && (
                <div style={{
                  fontSize: 12,
                  color: colors.textSecondary,
                  marginTop: 2
                }}>
                  {t('organization.role')}: {t(`organization.${userInfo.role}`)}
                </div>
              )}
            </div>
          )}

                      {/* Menu Items */}
            <div style={{ padding: '8px 0' }}>
              {/* Profile Option */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/profile');
                }}
              style={{
                width: '100%',
                padding: '12px 20px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 15,
                color: colors.text,
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = colors.backgroundLight}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
              </svg>
              {t('userMenu.profile')}
            </button>

            {/* Organization Management Option - Only for admins */}
            {userInfo?.role === 'admin' && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/organization');
                }}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 15,
                  color: colors.text,
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.backgroundLight}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-3.7 4.94c-.34.45-.54 1-.54 1.56V20h-2v-6c0-1.1.9-2 2-2h2c1.1 0 2 .9 2 2v6h-2z"/>
                </svg>
                {t('userMenu.organizationManagement')}
              </button>
            )}

            {/* Audit Logs Option - Only for admins */}
            {userInfo?.role === 'admin' && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/audit-logs');
                }}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 15,
                  color: colors.text,
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.backgroundLight}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
                {t('userMenu.auditLogs')}
              </button>
            )}

            {/* Upgrade Option - Only for admins */}
            {userInfo?.role === 'admin' && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/upgrade');
                }}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 15,
                  color: colors.text,
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.backgroundLight}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                {t('userMenu.upgradePlan')}
              </button>
            )}

            {/* Admin Panel Option - Only for system admins or admins of platform owner companies (e.g., Lizzard Solutions) */}
            {/* Only admins (role === 'admin') from platform owner companies can see this button */}
            {((userInfo?.role === 'admin' && isSystemAdmin(userInfo?.email)) || (userInfo?.role === 'admin' && userInfo?.isPlatformOwner)) && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/admin');
                }}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 15,
                  color: colors.text,
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.backgroundLight}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                {t('userMenu.adminPanel')}
              </button>
            )}

            {/* Dark Mode Toggle */}
            <button
              onClick={() => {
                toggleDarkMode();
                setIsOpen(false);
              }}
              style={{
                width: '100%',
                padding: '12px 20px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 15,
                color: colors.text,
                transition: 'background-color 0.2s ease, color 0.2s ease',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = colors.backgroundLight}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              {isDark ? (
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z"/>
                </svg>
              ) : (
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd"/>
                </svg>
              )}
              {isDark ? t('userMenu.lightMode') || 'Light Mode' : t('userMenu.darkMode') || 'Dark Mode'}
            </button>

            {/* Divider */}
            <div style={{
              height: 1,
              background: colors.borderLight,
              margin: '8px 20px'
            }} />

            {/* Logout Option */}
            <button
              onClick={() => {
                setIsOpen(false);
                handleLogout();
              }}
              style={{
                width: '100%',
                padding: '12px 20px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 15,
                color: colors.error,
                fontWeight: 500,
                transition: 'background-color 0.2s ease, color 0.2s ease',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = colors.error + '1a'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
              </svg>
              {t('auth.logout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 